"use client";

import { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Checkbox,
  Alert,
  AlertTitle,
  Box,
  Typography,
  FormControlLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Build as BuildIcon,
  Inventory as InventoryIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as ResolveIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import moment from "moment";

export function TarjetasTareas({
  tareas,
  obtenerEstadoTarea,
  onEditar,
  onMarcarInactiva,
  onEliminar,
  onResolverAlerta,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTarea, setSelectedTarea] = useState(null);

  const handleMenuClick = (event, tarea) => {
    setAnchorEl(event.currentTarget);
    setSelectedTarea(tarea);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTarea(null);
  };

  const getChipProps = (estado) => {
    switch (estado) {
      case "vencida":
        return { color: "error", icon: <ErrorIcon /> };
      case "por_vencer":
        return { color: "warning", icon: <ScheduleIcon /> };
      case "al_dia":
        return { color: "success", icon: <CheckCircleIcon /> };
      case "inactiva":
        return { color: "default", icon: <CancelIcon /> };
      default:
        return { color: "default" };
    }
  };

  const getEstadoTexto = (estado, vencimiento) => {
    switch (estado) {
      case "vencida":
        return "Vencida";
      case "por_vencer":
        const diasRestantes = vencimiento
          ? moment(vencimiento).diff(moment(), "days")
          : 0;
        return `Vence en ${diasRestantes} días`;
      case "al_dia":
        return "Al día";
      case "inactiva":
        return "Inactiva";
      default:
        return "Desconocido";
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {tareas.map((tarea) => {
          const estado = obtenerEstadoTarea(tarea);
          const chipProps = getChipProps(estado);

          return (
            <Grid item xs={12} sm={6} md={4} key={tarea.id}>
              <Card
                sx={{
                  opacity: estado === "inactiva" ? 0.6 : 1,
                  height: "100%",
                }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.4,
                        fontSize: 14,
                      }}
                    >
                      {tarea.tarea}
                    </Typography>
                  }
                  subheader={
                    tarea.periodo ? (
                      <Chip
                        label={tarea.periodo}
                        size="small"
                        sx={{ mt: 0.5, height: 20, fontSize: 11, bgcolor: "rgba(59,130,246,0.08)", color: "#1d4ed8" }}
                        variant="outlined"
                      />
                    ) : null
                  }
                  action={
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, tarea)}>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  {tarea.alerta_material && (
                    <Alert severity="warning" sx={{ mb: 2, p: 1 }}>
                      <AlertTitle sx={{ fontSize: "0.75rem" }}>
                        Alerta de Material
                      </AlertTitle>
                      <Typography variant="caption">
                        {moment(tarea.alerta_material).format("DD/MM/YYYY")} (
                        {(() => {
                          const dias = moment(tarea.alerta_material).diff(
                            moment(),
                            "days"
                          );
                          return dias > 0
                            ? `Faltan ${dias} días`
                            : dias === 0
                            ? "Es hoy"
                            : `Hace ${Math.abs(dias)} días`;
                        })()}
                        )
                      </Typography>
                    </Alert>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <PersonIcon
                      sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {tarea.responsable || "Sin asignar"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <EventIcon
                      sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {tarea.vencimiento
                        ? moment(tarea.vencimiento).format("DD/MM/YYYY")
                        : "Sin fecha"}
                    </Typography>
                  </Box>

                  {tarea.materiales && (
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                    >
                      <InventoryIcon
                        sx={{
                          mr: 1,
                          fontSize: 16,
                          color: "text.secondary",
                          mt: 0.2,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {tarea.materiales}
                      </Typography>
                    </Box>
                  )}

                  {tarea.herramientas && (
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                    >
                      <BuildIcon
                        sx={{
                          mr: 1,
                          fontSize: 16,
                          color: "text.secondary",
                          mt: 0.2,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {tarea.herramientas}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tarea.instructivohsse}
                          disabled
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">HSSE</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tarea.cert_especial}
                          disabled
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Cert. Especial
                        </Typography>
                      }
                    />
                  </Box>
                </CardContent>
                <CardActions>
                  <Chip
                    {...chipProps}
                    label={getEstadoTexto(estado, tarea.vencimiento)}
                    size="small"
                  />
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            onEditar(selectedTarea);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            onMarcarInactiva(selectedTarea);
            handleMenuClose();
          }}
        >
          <WarningIcon sx={{ mr: 1 }} />
          {Number(selectedTarea?.activa) === 1
            ? "Marcar como inactiva"
            : "Marcar como activa"}
        </MenuItem>
        {selectedTarea?.alerta_material && (
          <MenuItem
            onClick={() => {
              onResolverAlerta?.(selectedTarea);
              handleMenuClose();
            }}
          >
            <ResolveIcon sx={{ mr: 1, color: "#f59e0b" }} />
            Resolver alerta de materiales
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onEliminar(selectedTarea);
            handleMenuClose();
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>
    </Box>
  );
}
